/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { Maybe } from '@amazon/vinyl-util'
import { getStringFormattedDuration as getString } from '@/util/i18n/bundle.generated'

/**
 * Returns A time in the format of [hh:]mm:ss
 */
export function formatSecondsToHms(seconds: Maybe<number>, hoursOptional = true): string {
    if (seconds == null || !isFinite(seconds)) return ''
    let secondsStr = String(Math.floor(seconds % 60))
    if (secondsStr.length === 1) secondsStr = '0' + secondsStr
    let minutesStr = String(Math.floor((seconds % 3600) / 60))
    if (minutesStr.length === 1) minutesStr = '0' + minutesStr
    const hours = Math.floor(seconds / 3600)
    if (hoursOptional && hours === 0) {
        return `${minutesStr}:${secondsStr}`
    }
    let hoursStr = String(hours)
    if (hoursStr.length === 1) hoursStr = '0' + hoursStr
    return hoursStr + ':' + minutesStr + ':' + secondsStr
}

const numberFormatter = new Intl.NumberFormat()

export function formatSecondsToMinutes(seconds: Maybe<number>): string {
    if (seconds == null || !isFinite(seconds)) return ''
    return formatNumber(Math.floor(seconds / 60))
}

/**
 * "4 min 54 sec" / "1 hr 23 min" / "30 sec" — abbreviated duration
 * formatting routed through the localization bundle so plurals and
 * abbreviations come from translators. Mirrors hornet's
 * `dmusic_formatted_duration_abbr_*` keys.
 *
 * Picks the right key based on the magnitude:
 *   - ≥ 1 hour   → "1 hr 23 min" (seconds dropped)
 *   - < 1 hour with seconds > 0 → "4 min 54 sec" (or "30 sec" if minutes = 0)
 *   - < 1 hour with no seconds  → "5 min"
 */
export function formatDurationAbbr(seconds: Maybe<number>): string {
    if (seconds == null || !isFinite(seconds) || seconds < 0) return ''
    const totalSeconds = Math.floor(seconds)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const secs = totalSeconds % 60
    if (hours > 0) {
        return getString('dmusic_formatted_duration_abbr_hours_minutes', { hours, minutes }).value
    }
    if (secs > 0) {
        return getString('dmusic_formatted_duration_abbr_minutes_seconds', {
            minutes,
            seconds: secs,
        }).value
    }
    return getString('dmusic_formatted_duration_abbr_minutes', { minutes }).value
}

export function formatSecondsToHoursAndMinutes(seconds: Maybe<number>): string {
    if (seconds == null || !isFinite(seconds)) return ''
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)

    const parts: string[] = []
    if (hours > 0) parts.push(`${hours} HOUR${hours !== 1 ? 'S' : ''}`)
    if (minutes > 0 || hours === 0) parts.push(`${minutes} MINUTE${minutes !== 1 ? 'S' : ''}`)

    return parts.join(' ')
}

/**
 * Formats a number.
 */
export function formatNumber(n: Maybe<number>): string {
    if (n == null) return ''
    return numberFormatter.format(n)
}

/**
 * Strips HTML tags and decodes basic entities, returning a plain-text string.
 * Used for podcast descriptions which arrive with embedded markup
 * (`<p>`, `<a>`, `<br/>`).
 *
 * Uses the browser's HTML parser (via a temporary DOM element) so entities
 * like `&amp;` and `&#39;` decode correctly without a hand-rolled escape map.
 */
export function stripHtml(html: Maybe<string>): string {
    if (!html) return ''
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent.replace(/\s+/g, ' ').trim()
}
